import React, { useState, useEffect } from "react";
import { Box, Button, useToast, Text, Spinner } from "@chakra-ui/react";
import SearchableStoreSelect from "../../../Components/SearchableStoreSelect";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { PDFViewer } from "@react-pdf/renderer";
import PDFComponent from "../Files/PDFComponent";
import AlohaPDFComponent from "../Files/AlohaPDFComponent";
import axios from "axios";
import { getFormatterDate } from "../../../helpers/date";
import { handleAuthError } from "../../../utils/auth";
import {
  processGrossBreakDown,
  processHeaderData,
  processPaymentData,
  processSalesData,
  processdiscounts,
  processCashierBreakDown,
  processBillNoAndQty,
  processNonSalesData,
  processNonSalesDataWithTenderDesc,
  processAlohaHeaderReading,
  processAlohaSalesData,
  proccessAlohaDiscount,
  processAlohaGrossBreakDown,
  processAlohaCashierBreakDown,
  processAlohaPaymentData,
  processAlohaNonSalesData,
  processAlohaNonSalesDataWithTenderDesc,
  processAlohaBillNOQTY,
  processAlohaNonSales,
} from "../../../helpers/eodReport";

const currentTime = new Date();

const SalesLAC = ({ color }) => {
  const blink = keyframes`
  0% {opacity: 1;}
  50% {opacity: 0;}
  100% {opacity: 1;}
  `;
  const toast = useToast();
  const navigate = useNavigate();
  const role = localStorage.getItem("role");

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [data, setData] = useState();
  const [stores, setStores] = useState([]);
  const [userAssignedStores, setUserAssignedStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [dataLoading, setDataLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Preparing report...");

  const handleBranchChange = (e) => {
    const storeList = role === "store" ? filteredStores : stores;
    const selectedStore = storeList.find(
      (store) => store.locCode === e.target.value,
    );

    if (selectedStore && selectedStore.StoreName) {
      setSelectedBranch(selectedStore.locCode);
    }
    // Clear data when branch changes to prevent PDF component errors
    setData(null);
  };

  useEffect(() => {
    const fetchStores = async () => {
      setDataLoading(true);
      try {
        const resStores = await axios.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/maintenance/getBrands`,
          {
            brandId: 6, // LAC brand ID
          },
          { withCredentials: true },
        );
        setStores(resStores.data || []);

        // Get user's assigned stores
        const storeName = localStorage.getItem("storeName");
        let assignedStores = [];

        if (storeName) {
          try {
            // Try to parse as JSON array first
            const parsed = JSON.parse(storeName);
            assignedStores = Array.isArray(parsed) ? parsed : [storeName];
          } catch {
            // If parsing fails, it's a regular string
            assignedStores = [storeName];
          }
        }

        setUserAssignedStores(assignedStores);

        // Filter stores to only show assigned ones
        const userStores = resStores.data.filter((store) =>
          assignedStores.includes(store.StoreName),
        );
        setFilteredStores(userStores);

        if (role === "store") {
          // If user has only one store, auto-select it
          if (userStores.length === 1) {
            const store = userStores[0];
            if (store && store.StoreName) {
              setSelectedBranch(store.locCode);
            }
          }
          // If user has multiple stores, leave dropdown enabled for selection
        } else if (resStores.data.length > 0) {
          // Set first store as default for non-store roles
          setSelectedBranch(resStores.data[0].locCode);
        }
        setDataLoading(false);
      } catch (e) {
        // Check for authentication errors first
        if (e.response && e.response.status === 401) {
          handleAuthError(toast, navigate);
          return;
        }

        toast({
          title: "Error",
          position: "top-right",
          description: "Failed to fetch stores",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setDataLoading(false);
      }
    };
    fetchStores();
  }, [role, toast, navigate]);

  // Helper function to check if branch uses Aloha
  const isAlohaBranch = (locCode) => {
    const store = stores.find((s) => s.locCode === locCode);
    return store && store.POSSystem && store.POSSystem === "Aloha";
  };

  // Helper function to get the appropriate location code for Aloha API calls
  const getAlohaLocCode = (locCode) => {
    const store = stores.find((s) => s.locCode === locCode);
    // For Aloha stores, use the locCode directly for API calls
    // The database should contain the proper API code in locCode field
    return store ? store.locCode : locCode;
  };

  // Helper function to check if data is available
  const isDataAvailable = (data) => Array.isArray(data) && data.length > 0;

  const handleDateChange = async (date) => {
    setSelectedDate(date);
  };

  const handleGenerateReport = async (date, branch) => {
    if (!branch || branch === "") {
      toast({
        title: "Error",
        position: "top-right",
        description: "Please select a branch.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setDataLoading(true);
    const selected = getFormatterDate(date);
    const curr_date = getFormatterDate(currentTime);

    try {
      if (isAlohaBranch(branch)) {
        // Aloha branch flow
        const locCode = getAlohaLocCode(branch);
        const newDate = selected;

        setLoadingMessage("Fetching header readings...");
        const resHeader = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/aloha/getHeaderReading.aspx?brand=LA&qlik=${locCode}&datedfolder=${newDate.replace(/-/g, "")}`,
          { withCredentials: true },
        );
        if (!isDataAvailable(resHeader?.data))
          throw new Error("No header data found");
        const { totalGrossAmt, totalvat, openingData, closingData } =
          processAlohaHeaderReading(resHeader.data);

        setLoadingMessage("Retrieving sales data...");
        const resSales = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/aloha/getGrossSaleswithVat.aspx?brand=LA&qlik=${locCode}&datedfolder=${newDate.replace(/-/g, "")}`,
          { withCredentials: true },
        );
        if (!isDataAvailable(resSales?.data))
          throw new Error("No sales data found");
        const { salesData } = processAlohaSalesData(resSales.data);

        setLoadingMessage("Loading discount information...");
        const resDiscount = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/aloha/getDiscount.aspx?brand=LA&qlik=${locCode}&datedfolder=${newDate.replace(/-/g, "")}`,
          { withCredentials: true },
        );
        if (!isDataAvailable(resDiscount?.data))
          throw new Error("No discount data found");
        const { dataDiscount, dataDiscountVAT, dataDiscountVATE } =
          proccessAlohaDiscount(resDiscount.data);

        setLoadingMessage("Processing gross breakdown...");
        const resGrossBreakDown = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/aloha/getSaleswPerChannelithVat.aspx?brand=LA&qlik=${locCode}&datedfolder=${newDate.replace(/-/g, "")}`,
          { withCredentials: true },
        );
        if (!isDataAvailable(resGrossBreakDown?.data))
          throw new Error("No gross breakdown data found");
        const {
          dataGrossBreakDownReturned,
          totalGrossBreakDownVAT,
          totalGrossBreakDownVATE,
        } = processAlohaGrossBreakDown(resGrossBreakDown.data);

        setLoadingMessage("Collecting payment data...");
        const resPaymentData = await axios.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/aloha/getPaymentData.aspx?brand=LA&qlik=${locCode}&datedfolder=${newDate.replace(/-/g, "")}`,
          null,
          { withCredentials: true },
        );
        if (!isDataAvailable(resPaymentData?.data))
          throw new Error("No payment data found");
        const {
          totalPaymentCash,
          totalPaymentNonCash,
          paymentDataReturned,
          totalNonSalesCount,
          ChangeAmount,
          TenderAmount,
          VoidPaymentAmount,
          VoidPaymentCount,
          NonSalesPaymentDataReturned,
          totalNonCashQty,
        } = processAlohaPaymentData(resPaymentData.data);

        setLoadingMessage("Analyzing cashier breakdown...");
        const resCashierBreakDown = await axios.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/aloha/getCashAccountability.aspx?brand=LA&qlik=${locCode}&datedfolder=${newDate.replace(/-/g, "")}`,
          null,
          { withCredentials: true },
        );
        if (!isDataAvailable(resCashierBreakDown?.data))
          throw new Error("No cashier breakdown data found");
        const {
          cashierBreakDownReturned,
          totalCashierAmount,
          totalShortOver,
          shortOverDataReturned,
          totalShortOverNonCash,
          shortOverNonCashDataReturned,
          CMCash,
          CMFloat,
          CMOverShort,
          CMPickUp,
        } = processAlohaCashierBreakDown(resCashierBreakDown.data);

        setLoadingMessage("Retrieving billing information...");
        const resBillQtyNo = await axios.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/aloha/getBillNoAndQty.aspx?brand=LA&qlik=${locCode}&datedfolder=${newDate.replace(/-/g, "")}`,
          null,
          { withCredentials: true },
        );
        if (!isDataAvailable(resBillQtyNo?.data))
          throw new Error("No bill number and quantity data found");
        const { TCQty, OrderQty } = processAlohaBillNOQTY(resBillQtyNo.data);

        setLoadingMessage("Processing non-sales data...");
        const resMisc = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/aloha/getMisc.aspx?brand=LA&qlik=${locCode}&datedfolder=${newDate.replace(/-/g, "")}`,
          { withCredentials: true },
        );
        if (!isDataAvailable(resMisc?.data))
          throw new Error("No non-sales data found");
        const { NonSalesDataReturned } = processAlohaNonSalesData(resMisc.data);

        setLoadingMessage("Finalizing report data...");
        const resNonCashTenderDesk = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/aloha/getNonCash.aspx?brand=LA&qlik=${locCode}&datedfolder=${newDate.replace(/-/g, "")}`,
          { withCredentials: true },
        );
        if (!isDataAvailable(resNonCashTenderDesk?.data))
          throw new Error("No non-sales data with tender description found");
        const { NonSalesWithTenderDesc, totalNonSales, totalNonSalesCounter } =
          processAlohaNonSalesDataWithTenderDesc(resNonCashTenderDesk.data);

        const resNonSales = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/aloha/getNonsales.aspx?brand=LA&qlik=${locCode}&datedfolder=${newDate.replace(/-/g, "")}`,
          { withCredentials: true },
        );
        if (!isDataAvailable(resNonSales?.data))
          throw new Error("No non-sales data found");
        const { nonSalesData } = processAlohaNonSales(resNonSales.data);

        const resOrder = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/aloha/getorder.aspx?brand=LA&qlik=${locCode}&datedfolder=${newDate.replace(/-/g, "")}`,
          { withCredentials: true },
        );
        const orderData = isDataAvailable(resOrder?.data) ? resOrder.data : [];

        const costCenter = "-";

        const selectedStore = stores.find((store) => store.locCode === branch);
        setData({
          nonSalesData,
          dataDiscountVAT,
          dataDiscountVATE,
          BizDate: selected,
          totalNonSalesCounter,
          NonSalesPaymentDataReturned,
          totalShortOverNonCash,
          shortOverNonCashDataReturned,
          currDate: curr_date,
          PosId: locCode,
          openingData,
          closingData,
          totalGrossAmt,
          salesData,
          totalvat,
          dataDiscount,
          dataGrossBreakDownReturned,
          totalCashierAmount,
          totalPaymentCash,
          totalPaymentNonCash,
          paymentDataReturned,
          ChangeAmount,
          TenderAmount,
          totalGrossBreakDownVAT,
          totalGrossBreakDownVATE,
          cashierBreakDownReturned,
          VoidPaymentAmount,
          VoidPaymentCount,
          TCQty,
          OrderQty,
          NonSalesDataReturned,
          shortOverDataReturned,
          totalShortOver,
          totalNonCashQty,
          CMCash,
          CMFloat,
          CMOverShort,
          CMPickUp,
          NonSalesWithTenderDesc,
          totalNonSales,
          totalNonSalesCount,
          orderData,
          costCenter,
          storeName: selectedStore?.StoreName || branch,
          storeCode:
            selectedStore?.costcenter || selectedStore?.storecode || branch,
        });
      } else {
        // Regular LAC branch flow (existing logic)
        // Regular LAC branch flow (existing logic)
        setLoadingMessage("Fetching header readings...");
        const resDataHeader = await axios.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/getHeaderReading_la`,
          {
            branch: branch,
            date: selected,
          },
          { withCredentials: true },
        );

        if (!resDataHeader?.data) throw new Error("No header data found");
        const { openingData, closingData, totalGrossAmt, totalvat } =
          processHeaderData(resDataHeader.data);

        setLoadingMessage("Retrieving sales data...");
        const resSalesData = await axios.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/getSalesData_la`,
          {
            branch: branch,
            date: selected,
          },
          { withCredentials: true },
        );

        if (!resSalesData?.data) throw new Error("No sales data found");
        const { salesData } = processSalesData(resSalesData.data);

        setLoadingMessage("Loading discount information...");
        const resDiscountData = await axios.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/getDiscountData_la`,
          {
            branch: branch,
            date: selected,
          },
          { withCredentials: true },
        );

        let discountData;
        if (!resDiscountData?.data) {
          // Return default value when no discount data found
          discountData = [
            {
              LocCode: branch,
              taxcode: "VAT",
              discdesc: "-",
              discount: "0",
              qty: "0",
            },
          ];
        } else {
          discountData = resDiscountData.data;
        }

        const { dataDiscount, dataDiscountVAT, dataDiscountVATE } =
          processdiscounts(discountData);

        setLoadingMessage("Processing gross breakdown...");
        const resGrossBreakDown = await axios.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/getGrossBreakDown_la`,
          {
            branch: branch,
            date: selected,
          },
          { withCredentials: true },
        );

        if (!resGrossBreakDown?.data)
          throw new Error("No gross breakdown data found");
        const {
          dataGrossBreakDownReturned,
          totalGrossBreakDownVAT,
          totalGrossBreakDownVATE,
        } = processGrossBreakDown(resGrossBreakDown.data);

        setLoadingMessage("Collecting payment data...");
        const resPaymentData = await axios.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/getPaymentData_la`,
          { branch, date: selected },
          { withCredentials: true },
        );

        if (!resPaymentData?.data) throw new Error("No payment data found");
        const {
          totalPaymentNonCash,
          paymentDataReturned,
          totalNonSalesCount,
          ChangeAmount,
          TenderAmount,
          VoidPaymentAmount,
          VoidPaymentCount,
          NonSalesPaymentDataReturned,
          totalNonCashQty,
        } = processPaymentData(resPaymentData.data);

        setLoadingMessage("Analyzing cashier breakdown...");
        const resCashierBreakDown = await axios.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/getCashierBreakDown_la`,
          { branch, date: selected },
          { withCredentials: true },
        );

        if (!resCashierBreakDown?.data)
          throw new Error("No cashier breakdown data found");
        const {
          cashierBreakDownReturned,
          totalCashierAmount,
          totalShortOver,
          shortOverDataReturned,
          totalShortOverNonCash,
          shortOverNonCashDataReturned,
          CMCash,
          CMFloat,
          CMOverShort,
          CMPickUp,
        } = processCashierBreakDown(resCashierBreakDown.data);

        setLoadingMessage("Retrieving billing information...");
        const resBillNoQty = await axios.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/getBillNoAndQty_la`,
          { branch, date: selected },
          { withCredentials: true },
        );
        if (!resBillNoQty?.data)
          throw new Error("No bill number and quantity data found");
        const { TCQty, OrderQty } = processBillNoAndQty(resBillNoQty.data);

        setLoadingMessage("Processing non-sales data...");
        const resNonSalesData = await axios.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/getNonSalesData_la`,
          { branch, date: selected },
          { withCredentials: true },
        );

        if (!resNonSalesData?.data) throw new Error("No non-sales data found");
        const { NonSalesDataReturned } = processNonSalesData(
          resNonSalesData.data,
        );

        setLoadingMessage("Finalizing report data...");
        const resGetNonSalesDataWithTenderDesc = await axios.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/getNonSalesDataWithTenderDesc_la`,
          { branch, date: selected },
          { withCredentials: true },
        );
        if (!resGetNonSalesDataWithTenderDesc?.data)
          throw new Error("No non-sales data with tender description found");
        const { NonSalesWithTenderDesc, totalNonSales, totalNonSalesCounter } =
          processNonSalesDataWithTenderDesc(
            resGetNonSalesDataWithTenderDesc.data,
          );
        const { costCenter } = "-";
        const selectedStore = stores.find((store) => store.locCode === branch);
        setData({
          dataDiscountVAT,
          dataDiscountVATE,
          BizDate: selected,
          totalNonSalesCounter,
          NonSalesPaymentDataReturned,
          totalShortOverNonCash,
          shortOverNonCashDataReturned,
          currDate: curr_date,
          PosId: branch,
          openingData,
          closingData,
          totalGrossAmt,
          salesData,
          totalvat,
          dataDiscount,
          dataGrossBreakDownReturned,
          totalCashierAmount,
          totalPaymentNonCash,
          paymentDataReturned,
          ChangeAmount,
          TenderAmount,
          totalGrossBreakDownVAT,
          totalGrossBreakDownVATE,
          cashierBreakDownReturned,
          VoidPaymentAmount,
          VoidPaymentCount,
          TCQty,
          OrderQty,
          NonSalesDataReturned,
          shortOverDataReturned,
          totalShortOver,
          totalNonCashQty,
          CMCash,
          CMFloat,
          CMOverShort,
          CMPickUp,
          NonSalesWithTenderDesc,
          totalNonSales,
          totalNonSalesCount,
          costCenter: costCenter,
          storeName: selectedStore?.StoreName || branch,
          storeCode:
            selectedStore?.costcenter || selectedStore?.storecode || branch,
        });
      }
    } catch (err) {
      // Check for authentication errors first
      if (err.response && err.response.status === 401) {
        handleAuthError(toast, navigate);
        return;
      }

      toast({
        title: "Error",
        position: "top-right",
        description: err.message,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setDataLoading(false);
    }
  };

  return (
    <Box
      h="calc(100vh - 60px)"
      overflow="hidden"
      display="flex"
      flexDirection="column"
    >
      {/* Action Menu */}
      <Box
        mt={6}
        mb={0}
        pl={6}
        py={4}
        display="flex"
        gap={4}
        alignItems="center"
        flexDirection={{ base: "column", lg: "row" }}
        flexWrap={{ base: "wrap", lg: "nowrap" }}
        bg="gray.100"
        borderRadius="md"
        flexShrink={0}
      >
        <SearchableStoreSelect
          options={(role === "store" ? filteredStores : stores).map(
            (store) => ({
              value: store.locCode,
              label: store.StoreName,
            }),
          )}
          value={selectedBranch}
          onChange={(val) => handleBranchChange({ target: { value: val } })}
          isDisabled={
            (role === "store" && userAssignedStores.length <= 1) || dataLoading
          }
          placeholder={
            role === "store" && userAssignedStores.length <= 1
              ? stores.find((store) => store.locCode === selectedBranch)
                  ?.StoreName || "Loading..."
              : "Select Branch"
          }
        />

        <Box
          border="1px"
          borderColor="gray.300"
          borderRadius="md"
          display="flex"
          justifyContent="center"
          alignItems="center"
          bg="white"
          boxShadow="sm"
          h="40px"
          px={3}
        >
          <DatePicker
            disabled={dataLoading}
            minDate={new Date(2024, 3, 18)}
            maxDate={new Date()}
            selected={selectedDate}
            onChange={(date) => handleDateChange(date)}
            className="custom-date-picker"
            customInput={
              <div style={{ position: "relative" }}>
                <input
                  className="custom-date-picker-input"
                  value={selectedDate.toLocaleDateString()}
                  readOnly
                />
                <img
                  src="/calendar.svg"
                  className="calendar-icon"
                  alt="Calendar Icon"
                  style={{
                    position: "absolute",
                    right: "20px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: "20px",
                    height: "auto",
                  }}
                />
              </div>
            }
          />
        </Box>

        <Button
          bg="gray.700"
          color="white"
          size="md"
          onClick={() => handleGenerateReport(selectedDate, selectedBranch)}
          isDisabled={dataLoading}
          _hover={{ bg: "gray.600" }}
          boxShadow="sm"
        >
          Generate Report
        </Button>

        <Button
          bg="gray.700"
          color="white"
          size="md"
          _hover={{ bg: "gray.600" }}
          boxShadow="sm"
          isDisabled={!data || dataLoading}
        >
          {data ? (
            <PDFDownloadLink
              document={
                isAlohaBranch(selectedBranch) ? (
                  <AlohaPDFComponent data={data} />
                ) : (
                  <PDFComponent data={data} />
                )
              }
              fileName={`EODReport-${selectedBranch}-${getFormatterDate(selectedDate)}.pdf`}
            >
              {({ loading }) =>
                loading ? "Loading document..." : "Download Report"
              }
            </PDFDownloadLink>
          ) : (
            "Download Report"
          )}
        </Button>
      </Box>

      {/* PDF Viewer */}
      <Box mt={0} mb={8} height="80vh">
        {dataLoading ? (
          <Box
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="blue.500"
              size="xl"
            />
            <Text
              css={{ animation: `${blink} 1s linear infinite` }}
              marginLeft={4}
            >
              {loadingMessage}
            </Text>
          </Box>
        ) : (
          <PDFViewer
            showToolbar={false}
            style={{ width: "100%", height: "100%" }}
          >
            {data &&
              (isAlohaBranch(selectedBranch) ? (
                <AlohaPDFComponent data={data} />
              ) : (
                <PDFComponent data={data} />
              ))}
          </PDFViewer>
        )}
      </Box>
    </Box>
  );
};

export default SalesLAC;
